import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {getStaffMembersWithParentMeetingSlotsTeacherSummaries, StoreState} from '@redux/reducers';
import {School} from '@model/school';
import {combineLatest, concat, map, Observable, switchMap, take} from 'rxjs';
import {Profile} from '@model/profile';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {StaffMember} from '@model/staffMember';
import {DateTime} from 'luxon';
import {ParentMeetingService} from '@services/parent-meeting.service';
import {ParentMeetingSlotDeleted, ParentMeetingSlotSaved, ParentMeetingSlotsTeacherSummariesLoaded, ParentMeetingSlotUnbooked} from '@redux/actions/parent-meeting.action';
import {ParentMeetingSlot} from '@model/parent-meeting-slot';
import {MatSnackBar} from '@angular/material/snack-bar';
import {StudentSearchData} from '@model/student-search-data';
import {Page} from '@model/page';
import {StudentView} from '@model/student-view';
import {SchoolService} from '@services/school.service';
import {ParentsMeetingsSlotsTeacherSummariesWithStaffMember} from '@model/parents-meetings-slots-teacher-summaries';
import {select} from '@ngrx/store';

@Injectable()
export class ParentMeetingFacadeService {

  school$: Observable<School>;
  profile$: Observable<Profile>;
  staffMembers$: Observable<StaffMember[]>;
  parentMeetingSlots$: Observable<ParentMeetingSlot[]>;
  isAdminStaffMember$: Observable<boolean>;
  studentSearchResult$: Observable<Page<StudentView>>;
  isMobile$: Observable<boolean>;
  parentMeetingSlotsTeacherSummaries$: Observable<ParentsMeetingsSlotsTeacherSummariesWithStaffMember[]>;

  constructor(private _store: Store<StoreState>,
              private _sharedFacade: SharedFacadeService,
              private _parentMeetingService: ParentMeetingService,
              private _schoolService: SchoolService,
              private _snackBar: MatSnackBar) {
    this.school$ = this._sharedFacade.school$;
    this.profile$ = this._sharedFacade.profile$;
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.parentMeetingSlots$ = this._sharedFacade.parentMeetingSlots$;
    this.isAdminStaffMember$ = this._sharedFacade.isAdminStaffMember$;
    this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.parentMeetingSlotsTeacherSummaries$ = this._store.pipe(select(getStaffMembersWithParentMeetingSlotsTeacherSummaries));
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  generateMeetingSlots(staffMember: StaffMember, meetingSlots: DateTime[]) {
    this._snackBar.open('Création des réunions de parents ...');
    this.school$.pipe(
      take(1),
      switchMap(school => {
        const observables = meetingSlots.map(meetingSlot => {
          return this._parentMeetingService.createParentMeetingSlot(school.id, {
            startTime: meetingSlot,
            scheduledByStaffMemberId: staffMember.id,
            scheduledByStaffMemberFirstName: staffMember.firstName,
            scheduledByStaffMemberLastName: staffMember.lastName,
            bookableSlot: true,
            booked: false
          });
        });
        return concat(...observables);
      })
    ).subscribe({
      next: parentMeetingSlot => {
        this._store.dispatch(ParentMeetingSlotSaved({parentMeetingSlot}));
      },
      complete: () => this._snackBar.open('Vos réunions de parents sont crées.', '', {duration: 5000})
    });
  }

  loadParentMeetingSlots(staffMemberId: number, from?: DateTime, to?: DateTime) {
    this._sharedFacade.loadParentMeetingSlots(staffMemberId, from, to);
  }

  deleteParentMeetingSlot(parentMeetingSlotId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        return this._parentMeetingService.deleteParentMeetingSlot(school.id, parentMeetingSlotId);
      })
    ).subscribe(() => this._store.dispatch(ParentMeetingSlotDeleted({parentMeetingSlotId})));
  }

  saveParentMeetingSlot(parentMeetingSlot: ParentMeetingSlot) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        return this._parentMeetingService.updateParentMeetingSlot(school.id, parentMeetingSlot.id, parentMeetingSlot.bookableSlot);
      })
    ).subscribe(_ => this._store.dispatch(ParentMeetingSlotSaved({parentMeetingSlot})));
  }

  bookParentMeetingSlot(parentMeetingSlotId: string, student: StudentView) {
    combineLatest([this.school$, this.profile$]).pipe(
      take(1),
      switchMap(([school, profile]) => {
        return this._parentMeetingService.bookParentMeetingSlot(school.id, parentMeetingSlotId, student.id).pipe(
          map(_ => {
            return {
              id: parentMeetingSlotId,
              booked: true,
              concernedStudentId: student.id,
              concernedStudentFirstName: student.firstName,
              concernedStudentLastName: student.lastName,
              bookedByStaffMemberId: profile.id,
              bookedByStaffMemberFirstName: profile.firstName,
              bookedByStaffMemberLastName: profile.lastName
          } as ParentMeetingSlot})
        );
      })
    ).subscribe(parentMeetingSlot => this._store.dispatch(ParentMeetingSlotSaved({parentMeetingSlot})));
  }

  unbookParentMeetingSlot(parentMeetingSlotId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        return this._parentMeetingService.unbookParentMeetingSlot(school.id, parentMeetingSlotId);
      })
    ).subscribe(_ => this._store.dispatch(ParentMeetingSlotUnbooked({parentMeetingSlotId})));
  }

  searchStudents(searchData: StudentSearchData): void {
    this._sharedFacade.searchStudents(searchData);
  }
  
  loadParentsMeetingsSlotsTeachersSummaries(from?:DateTime, to?:DateTime) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._schoolService.loadParentsMeetingsSlotsTeachersSummaries(school.id, from, to))
    ).subscribe(
      summaries => this._store.dispatch(ParentMeetingSlotsTeacherSummariesLoaded({ summaries }))
    );
  }

  resetParentMeetingSlots() {
    this._sharedFacade.resetParentMeetingSlots();
  }
}
